/**
 * An Area's brief on the SPA (slice 6d spec §5.2, §8.5): the file read into
 * facts, notes and other lines, each with its place in the file, and the one
 * line a Sections edit changes written back, every other line as it was. The
 * Worker's parser is `worker/domain/brief.ts`; this one keeps line numbers.
 * Pure, so it's tested on its own (`brief.test.ts`).
 */
/** The whole file, in characters (spec §5.2). */
export const BRIEF_MAX = 4_000;

type Section = "facts" | "notes";

/** A fact or a note: its line in the file (`at`), and the line as written (`raw`). */
export type BriefLine = { at: number; raw: string; text: string; date: string | null };

export type BriefParts = {
  title: string;
  facts: BriefLine[];
  notes: BriefLine[];
  /** Lines outside the shape (another heading, a paragraph): kept as written. */
  other: BriefLine[];
};

const BULLET = /^\s*[-*] /;
const NOTE_DATE = /^(\d{4}-\d{2}-\d{2}):\s*(.*)$/;

function sectionOf(line: string): Section | "other" {
  const heading = line.trim().toLowerCase();
  return heading === "## facts" ? "facts" : heading === "## notes" ? "notes" : "other";
}

function lineOf(at: number, raw: string, section: Section): BriefLine {
  const text = raw.replace(BULLET, "");
  const m = section === "notes" ? NOTE_DATE.exec(text) : null;
  return m ? { at, raw, text: m[2], date: m[1] } : { at, raw, text, date: null };
}

/** The brief's facts, notes and anything else, as the Worker reads them. */
export function parseBrief(content: string): BriefParts {
  const parts: BriefParts = { title: "", facts: [], notes: [], other: [] };
  let section: Section | "other" = "other";
  content.split("\n").forEach((raw, at) => {
    if (/^# /.test(raw) && !parts.title) {
      parts.title = raw.slice(2).trim();
    } else if (/^##\s/.test(raw)) {
      section = sectionOf(raw);
      if (section === "other") parts.other.push({ at, raw, text: raw, date: null });
    } else if (raw.trim()) {
      if (section !== "other" && BULLET.test(raw)) parts[section].push(lineOf(at, raw, section));
      else parts.other.push({ at, raw, text: raw, date: null });
    }
  });
  return parts;
}

const oneLine = (text: string) => text.replace(/\s+/g, " ").trim();

/** A fact's line, or a note's with its date. */
export function rawLine(text: string, date: string | null = null): string {
  return date ? `- ${date}: ${oneLine(text)}` : `- ${oneLine(text)}`;
}

/** The file with line `at` made `raw`, or taken out when `raw` is null. */
export function replaceLine(content: string, at: number, raw: string | null): string {
  const lines = content.split("\n");
  if (raw === null) lines.splice(at, 1);
  else lines[at] = raw;
  return lines.join("\n");
}

/**
 * The file with a line added, as the agent's `add` does (Worker's
 * `applyBriefEdit`): a fact at the end of Facts, a note first under Notes
 * (newest first). A missing heading is added.
 */
export function addLine(content: string, section: Section, raw: string): string {
  const heading = section === "facts" ? "## facts" : "## notes";
  const lines = content.replace(/\n+$/, "").split("\n");
  const at = lines.findIndex((l) => l.trim().toLowerCase() === heading);
  if (at === -1)
    return [...lines, "", section === "facts" ? "## Facts" : "## Notes", raw, ""].join("\n");
  lines.splice(section === "notes" ? at + 1 : sectionEnd(lines, at), 0, raw);
  return `${lines.join("\n")}\n`;
}

/** After a section's last line: before the next heading and the blank lines over it. */
function sectionEnd(lines: string[], heading: number): number {
  let end = heading + 1;
  while (end < lines.length && !/^#{1,6}\s/.test(lines[end])) end++;
  while (end > heading + 1 && !lines[end - 1].trim()) end--;
  return end;
}

/** Today in the workspace's timezone, as a note's date: "2026-10-16". */
export function todayIn(timeZone: string, now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** A note's date as the list shows it: "Oct 16". */
export function noteDay(date: string): string {
  const at = Date.parse(`${date}T12:00:00Z`);
  if (Number.isNaN(at)) return date;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
  }).format(at);
}

const count = new Intl.NumberFormat("en-US");

/** The footer's "1,820 of 4,000 characters". */
export function lengthLine(length: number, short = false): string {
  return `${count.format(length)} of ${count.format(BRIEF_MAX)}${short ? "" : " characters"}`;
}

/** Why the brief can't be saved, or null. */
export function tooLong(length: number): string | null {
  return length > BRIEF_MAX
    ? "That's over 4,000 characters. Shorten a line or move steps into a doc."
    : null;
}

/** Who made a version, as the API names them. */
export type Author = { kind: "person" | "agent"; id: string | null; name: string };

/** One version of the brief and what it changed from the one before. */
export type BriefChange = {
  version: number;
  updated_by: Author;
  updated_at: string;
  lines: { removed: string[]; added: string[] };
  can_undo: boolean;
};

/** The newest change that wrote `raw` as it is now, from the newest-first list. */
export function lastChange(raw: string, changes: readonly BriefChange[]): BriefChange | null {
  return changes.find((c) => c.lines.added.includes(raw)) ?? null;
}

/** A line's text as a person reads it: no bullet, no note's date. */
export function plainLine(raw: string): string {
  const text = raw.replace(BULLET, "");
  return NOTE_DATE.exec(text)?.[2] ?? text;
}

/**
 * A crew change from the last 7 days under its line (spec §8.5): the newest
 * agent's change that wrote it, and the line it replaced. Null for a line a
 * person wrote last, or one no recent change touched.
 */
export function crewChange(
  raw: string,
  recent: readonly BriefChange[],
): { change: BriefChange; was: string | null } | null {
  const change = lastChange(raw, recent);
  if (change?.updated_by.kind !== "agent") return null;
  const was =
    change.lines.removed.length === 1
      ? changedPart(plainLine(change.lines.removed[0]), plainLine(raw))
      : null;
  return { change, was };
}

/** The words of `before` that `after` changed: "$2,500." of "The budget is $2,500. Ask…". */
export function changedPart(before: string, after: string): string {
  const a = before.split(" ");
  const b = after.split(" ");
  let start = 0;
  while (start < Math.min(a.length, b.length) && a[start] === b[start]) start++;
  let end = 0;
  const room = Math.min(a.length, b.length) - start;
  while (end < room && a[a.length - 1 - end] === b[b.length - 1 - end]) end++;
  return a.slice(start, a.length - end).join(" ") || before;
}

/** "Chief of Staff changed this today. Was $2,500." */
export function crewChangeLine(name: string, when: string, was: string | null): string {
  if (!was) return `${name} added this ${when}.`;
  return `${name} changed this ${when}. Was ${was}${/[.!?]$/.test(was) ? "" : "."}`;
}

/** "today", "yesterday", "on Mon", "on Sep 3", in the workspace's timezone. */
export function dayWord(iso: string, timeZone: string, now = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const days = Math.round(
    (Date.parse(`${todayIn(timeZone, now)}T00:00:00Z`) -
      Date.parse(`${todayIn(timeZone, at)}T00:00:00Z`)) /
      86_400_000,
  );
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  const format =
    days < 7 ? { weekday: "short" as const } : { month: "short" as const, day: "numeric" as const };
  return `on ${new Intl.DateTimeFormat("en-US", { timeZone, ...format }).format(at)}`;
}

const isNote = (raw: string) => NOTE_DATE.test(raw.replace(BULLET, ""));
const kindOf = (raw: string) =>
  isNote(raw) ? "note" : raw.trim().startsWith("#") ? "heading" : "fact";

/** A version in Recent changes: "Changed a fact", "Added a note", "Removed a fact", "Edited the brief". */
export function changeSummary(lines: BriefChange["lines"]): string {
  const { removed, added } = lines;
  if (added.length === 1 && removed.length === 1 && kindOf(added[0]) === kindOf(removed[0])) {
    return `Changed a ${kindOf(added[0])}`;
  }
  if (added.length === 1 && removed.length === 0) return `Added a ${kindOf(added[0])}`;
  if (removed.length === 1 && added.length === 0) return `Removed a ${kindOf(removed[0])}`;
  return "Edited the brief";
}

/** The Area page card's brief: its facts, and each mentioned doc's title now by id. */
export function cardBrief(brief: { facts: string[]; titles?: Record<string, string> } | null) {
  return { facts: brief?.facts ?? [], titles: new Map(Object.entries(brief?.titles ?? {})) };
}

/**
 * The Area page's card, folded (spec §8.2): a desktop shows the first three
 * facts cut to three lines, a phone the first two cut to two.
 */
export const CARD_FOLD = { desktop: { facts: 3 }, phone: { facts: 2 } } as const;

/**
 * The thread's line for a turn that changed an Area's brief (`updated_files`
 * has `area:<id>`): "Updated the Garage gym brief", to its editor. Areas not
 * in the list (an archived one) are left out.
 */
export function briefNotes(
  updatedFiles: readonly string[] | undefined,
  areas: ReadonlyArray<{ id: string; slug: string; label: string }>,
): { text: string; href: string }[] {
  return (updatedFiles ?? []).flatMap((f) => {
    const area = f.startsWith("area:") ? areas.find((a) => a.id === f.slice(5)) : undefined;
    return area ? [{ text: `Updated the ${area.label} brief`, href: briefHref(area.slug) }] : [];
  });
}

export const briefHref = (slug: string) => `/areas/${slug}/brief`;

/**
 * One Sections edit: the `n`th fact or note (null: a new one) made `draft`.
 * A note keeps its date; a new one is dated `today`. An `n` the file no
 * longer has (a newer brief came) adds the line instead.
 */
/** A line being changed (`n`, null for a new one), and its text when opened, which finds it again. */
export type LineEdit = { section: Section; n: number | null; draft: string; raw?: string };

/** `edit` with its line's text when opened, so others' saves can't move it to another line. */
export function pinEdit(content: string, edit: LineEdit): LineEdit {
  const line = edit.n === null ? undefined : parseBrief(content)[edit.section][edit.n];
  return line ? { ...edit, raw: line.raw } : edit;
}

/**
 * The Sections editor's state over `content` now: the open edit on its line
 * (`live`, null once the line is gone, which is a conflict too), the line as
 * it's written (`sent`: `links` makes its `@Title`s links), what the file
 * would be, and whether it can be saved.
 */
export function lineView(
  content: string,
  opened: LineEdit | null,
  clash: boolean,
  today: string,
  links: (edit: LineEdit) => LineEdit = (e) => e,
) {
  const live = opened && relocate(content, opened);
  const sent = live && links(live);
  const next = sent ? applyEdit(content, sent, today) : content;
  return {
    live,
    sent,
    editing: live ?? opened,
    conflict: clash || (opened !== null && live === null),
    next,
    saveable: Boolean(live?.draft.trim()) && !tooLong(next.length),
  };
}

/**
 * After `sent` saved into `saved`: the box closes, unless more was typed
 * meanwhile (`now`), when it stays open on the saved line with those words.
 */
export function afterSave(now: LineEdit | null, sent: LineEdit, saved: string): LineEdit | null {
  return now && now.draft !== sent.draft ? { ...savedEdit(saved, sent), draft: now.draft } : null;
}

/** `edit` pinned to its line in `saved`, the file it was just saved into: a new note is first, a new fact last. */
export function savedEdit(saved: string, edit: LineEdit): LineEdit {
  const n = edit.n ?? (edit.section === "notes" ? 0 : parseBrief(saved).facts.length - 1);
  return pinEdit(saved, { ...edit, n });
}

/**
 * `edit` against `content` now (a live save may have moved its line): `n`
 * the nearest line with its text, or null once that line is gone.
 */
export function relocate(content: string, edit: LineEdit): LineEdit | null {
  if (edit.n === null || edit.raw === undefined) return edit;
  const lines = parseBrief(content)[edit.section];
  if (lines[edit.n]?.raw === edit.raw) return edit;
  let best = -1;
  lines.forEach((l, i) => {
    if (l.raw === edit.raw && (best < 0 || Math.abs(i - edit.n!) < Math.abs(best - edit.n!))) {
      best = i;
    }
  });
  return best < 0 ? null : { ...edit, n: best };
}

/** The whole file after `edit`, or after removing its line. */
export function applyEdit(content: string, edit: LineEdit, today: string, remove = false): string {
  const line = edit.n === null ? undefined : parseBrief(content)[edit.section][edit.n];
  if (remove) return line ? replaceLine(content, line.at, null) : content;
  const date = edit.section === "notes" ? (line ? line.date : today) : null;
  const raw = rawLine(edit.draft, date);
  return line ? replaceLine(content, line.at, raw) : addLine(content, edit.section, raw);
}

/** A version's author: "You" for the person reading. */
export function authorLabel(author: Author, meId: string): string {
  return author.kind === "person" && author.id === meId ? "You" : author.name;
}

/** What Change a fact's sheet says for an edit: its title, the line's noun, and its last change. */
export function editTarget(content: string, edit: LineEdit | null, recent: readonly BriefChange[]) {
  const section = edit?.section ?? "facts";
  const noun = section === "facts" ? "fact" : "note";
  const line = edit?.n == null ? undefined : parseBrief(content)[section][edit.n];
  const existing = edit !== null && edit.n !== null;
  return {
    title: `${existing ? "Change" : "Add"} a ${noun}`,
    noun,
    existing,
    last: line ? lastChange(line.raw, recent) : null,
  };
}

type FirstVersion = Pick<BriefChange, "version" | "updated_by" | "updated_at">;

/** All N versions' pages as one list, and the first version once the last page is in. */
export function versionList(
  pages: ReadonlyArray<{ versions: BriefChange[]; first: FirstVersion | null }> | undefined,
  more: boolean,
): { versions: BriefChange[]; first: FirstVersion | null } {
  const all = pages ?? [];
  return {
    versions: all.flatMap((p) => p.versions),
    first: more ? null : (all.at(-1)?.first ?? null),
  };
}
